<template>
  <StoreCart>
    <template #default="{ total, minimumAmount }">
      <!-- Alert if cart has minimum amount items -->
      <div
        v-if="minimumAmount > total"
        class="relative flex px-4 py-3 text-sm rounded bg-warning-100 text-warning-600 md:items-center"
        role="alert"
      >
        <Icon name="IcTwotoneWarning" class="flex flex-shrink-0 w-8 h-8 me-3" />
        <div class="items-center justify-between w-full md:flex">
          <p>
            Please add items worth
            <Amount :value="minimumAmount - total" class="font-medium" /> or
            more to proceed to checkout.
          </p>

          <VyButton
            class="mt-1 ms-2 md:mt-0"
            size="sm"
            color="warning"
            shape="rounded"
            theme="solid"
            :label="$t('add_more_items')"
            :to="{ name: 'biz' }"
          />
        </div>
      </div>
      <!-- Delivery Location -->
      <StoreShippingLocations />

      <!-- Items -->
      <StoreCartSection :title="$t('qty_items', [cartStore.productCount])">
        <template #right>
          <p
            class="py-2 text-xs font-medium cursor-pointer text-danger-500"
            @click="emptyCart"
          >
            {{ $t("empty_cart") }}
          </p>
        </template>

        <div v-if="isServer" class="divide-y">
          <ShimmerCartProduct v-for="n in 4" :key="`shimmer-${n}`" />
        </div>
        <VList
          ref="list"
          endpoint="GET_CART"
          :request-payload="{ noCache: true }"
          :per-page="-1"
          :params="params"
        >
          <template #loading-page>
            <div
              class="absolute inset-0 z-20 flex items-center justify-center bg-white bg-opacity-70 dark:bg-opacity-30"
            >
              <Loader class="w-8 h-8" />
            </div>
          </template>

          <template #loading>
            <div class="divide-y">
              <ShimmerCartProduct v-for="n in 4" :key="`shimmer-${n}`" />
            </div>
          </template>

          <template #default="{ items }">
            <div class="divide-y dark:bg-gray-900">
              <StoreCartProduct
                v-for="cartProduct in items"
                :id="parseInt(cartProduct.product.id)"
                :key="`product-${cartProduct.product.id}`"
                :name="cartProduct.product.name"
                :media="cartProduct.product.media"
                :price="cartProduct.product.price"
                :regular-price="cartProduct.product.regularPrice"
                :measurement="cartProduct.product.measurementWithUnit"
                :stock="cartProduct.product.stock"
                :is-out-of-stock="cartProduct.product.isOutOfStock"
                :manage-inventory="cartProduct.product.manageInventory"
                :allow-qty="cartProduct.product.allowQty"
                :charges="cartProduct.charges"
                :type="cartProduct.product.type"
                :message="cartProduct.message"
                :variant="cartProduct.product.variant"
                :sub-total="cartProduct.subTotal"
                :regular-sub-total="cartProduct.regularSubTotal"
                @refresh="$refs.list.refresh()"
              />
            </div>
          </template>
        </VList>
      </StoreCartSection>

      <!-- Addons -->
      <StoreCheckoutAddons />
    </template>

    <template #action>
      <!-- Next -->

      <VyButton
        size="lg"
        color="warning"
        shape="rounded"
        theme="solid"
        direction="rowReverse"
        :label="$t('address')"
        icon="MdiArrowRight"
        :to="{ name: 'biz-cart-address' }"
      />
    </template>
  </StoreCart>
</template>

<script setup>
import { useCartStore } from "@/store/cart.js";
import { useVepaarStore } from "@/store/store.js";

definePageMeta({
  layout: "cart",
  scrollToTop: true,
});

const isServer = import.meta.client ? false : true;

const hash = useVepaarStore().hash;

const cartStore = useCartStore();

const params = computed(() => {
  return {
    variables: {
      hash: hash,
      id: cartStore.sessionId,
    },
    result: result,
  };
});

/**
 * This function converts the card response to table format.
 */
function result({
  data: {
    cartOverview: { cartProducts },
  },
}) {
  return {
    items: cartProducts,
    count: cartProducts.length,
  };
}

function emptyCart() {
  const check = confirm(
    "Are you sure you want to remove all items from the cart?"
  );
  if (check) {
    useCartStore().reset();
  }
}
</script>
